import {
  AxiosHeaders,
  AxiosInstance,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
  Method,
} from 'axios';
import { serializeHttpParams } from '../../utils/url-params';
import { getBaseUrl } from '../../utils/base-url';
import { AccessToken } from '../access-token';
import { UserCurrentCompany } from '../user-company';
import HttpError from './HttpError';

export interface HttpClientInstance {
  get: <ParamsT = any, ResponseT = any>(
    url: string,
    params?: ParamsT,
    abortSignal?: AbortSignal,
    headers?: AxiosRequestHeaders
  ) => AxiosPromise<ResponseT>;
  download: <ParamsT = any, ResponseT = any>(
    url: string,
    params?: ParamsT,
    abortSignal?: AbortSignal
  ) => AxiosPromise<ResponseT>;
  post: <ParamsT = any, ResponseT = any>(
    url: string,
    data?: ParamsT,
    abortSignal?: AbortSignal,
    headers?: AxiosRequestHeaders
  ) => AxiosPromise<ResponseT>;
  put: <ParamsT = any, ResponseT = any>(
    url: string,
    data?: ParamsT,
    abortSignal?: AbortSignal,
    headers?: AxiosRequestHeaders
  ) => AxiosPromise<ResponseT>;
  patch: <ParamsT = any, ResponseT = any>(
    url: string,
    data?: ParamsT,
    abortSignal?: AbortSignal,
    headers?: AxiosRequestHeaders
  ) => AxiosPromise<ResponseT>;
  delete: <ParamsT = any, ResponseT = any>(
    url: string,
    data?: ParamsT,
    abortSignal?: AbortSignal,
    headers?: AxiosRequestHeaders
  ) => AxiosPromise<ResponseT>;
}

export class HttpServiceConfigurator {
  static getHeaders(baseConfig?: AxiosRequestConfig) {
    const headers = new AxiosHeaders();
    if (baseConfig?.url) {
      const isFormData = baseConfig.data instanceof FormData;
      headers.set('Client-Device', 'web');
      headers.set('Content-Type', isFormData ? 'multipart/formdata' : 'application/json');
    }

    const authorization = AccessToken.getToken();
    const currentCompanyId = UserCurrentCompany.getCompany();
    if (authorization) headers.set('authorization', authorization);
    if (currentCompanyId) {
      headers.set('x-oh-auth-company-id', currentCompanyId);
    }
    return headers;
  }

  getHttpClientConfig() {
    return {
      paramsSerializer: {
        serialize: serializeHttpParams,
      },
      baseURL: getBaseUrl(),
    };
  }

  async prepareRequestConfig(baseConfig: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
    const config = {
      ...baseConfig,
      ...this.getHttpClientConfig(),
      headers: new AxiosHeaders({
        ...baseConfig.headers,
        ...HttpServiceConfigurator.getHeaders(baseConfig),
      })
    };
    config.headers.set('X-Oh-App-Version', process.env.APP_VERSION);
    return config;
  }

  prepareHttpSuccessResponse(response: AxiosResponse) {
    AccessToken.setTokenFromHttpResponse(response);
    return response;
  }

  async prepareHttpErrorResponse(error: any) {

    const parseBlob = async (blob: Blob): Promise<Record<string, any> | null> => {
      const text = await blob.text();
      try {
        return JSON.parse(text);
      } catch (e) {
        return null;
      }
    };

    if (error.response?.data instanceof Blob) {
      error.response.data = await parseBlob(error.response.data);
    }

    return Promise.reject(new HttpError(error));
  }

  createInterface(clientInstance: AxiosInstance): HttpClientInstance {
    const createRequestHandler =
      <ParamsT = any, ResponseT = any>(method: Method) =>
        (url: string, data: ParamsT, abortSignal?: AbortSignal, headers?: AxiosRequestHeaders): AxiosPromise<ResponseT> => {
          return clientInstance(url, {
            method,
            data,
            signal: abortSignal,
            headers
          });
        };

    return {
      get: (url: string, params: any, abortSignal?: AbortSignal, headers?: AxiosRequestHeaders) =>
        clientInstance(url, {
          method: 'get',
          params,
          signal: abortSignal,
          headers
        }),
      download: (url: string, params?: any, abortSignal?: AbortSignal) =>
        clientInstance(url, { method: 'get', params, responseType: 'blob', signal: abortSignal }),
      post: createRequestHandler('post'),
      put: createRequestHandler('put'),
      patch: createRequestHandler('patch'),
      delete: createRequestHandler('delete'),
    };
  }
}
